import {
  PERSISTENCE_KEY_V1, PERSISTENCE_KEY_V2,
  migrateV1ToV2, type PersistedV2, type V1Snapshot,
  type GameId, type TableId, type TablePrefs, type PersistedTable, type ActiveGame,
} from '@casino/core';
import { DEFAULT_RULES, type TableRules } from '@casino/craps-engine';
import { STARTING_BANKROLL_CENTS } from './config.js';

// Standard tier rules — typical Strip low-stakes floor. The High Limit and
// Salon Privé tiers below offer meaningfully better rules.
const CRAPS_STANDARD_RULES: TableRules = {
  ...DEFAULT_RULES,
  tableMin: 2_500,
  tableMax: 100_000,
  atsPayouts: { small: 30, tall: 30, all: 150 },
};

// High Limit tier — what Standard used to be. 3-4-5× → richer ATS payouts.
const CRAPS_HIGH_LIMIT_RULES: TableRules = {
  ...DEFAULT_RULES,
  tableMin: 10_000,    // $100
  tableMax: 1_000_000, // $10k
  atsPayouts: { small: 35, tall: 35, all: 175 },
};

// Salon Privé tier — High Limit + 100× odds.
const CRAPS_WHALE_RULES: TableRules = {
  ...CRAPS_HIGH_LIMIT_RULES,
  oddsCap: '100x',
  tableMin: 100_000,    // $1k
  tableMax: 10_000_000, // $100k
  atsPayouts: { small: 40, tall: 40, all: 200 },
};

function lockedTable<T>(rules: T, selectedDenomCents: number): PersistedTable<T> {
  return {
    rules,
    prefs: { selectedDenomCents },
    unlocked: false,
    unlockSeenInLobby: false,
    unlockedAt: null,
  };
}

function unlockedStandardTable<T>(rules: T, selectedDenomCents: number): PersistedTable<T> {
  return {
    rules,
    prefs: { selectedDenomCents },
    unlocked: true,
    unlockSeenInLobby: true,
    unlockedAt: 0,
  };
}

function freshDefaults(): PersistedV2 {
  return {
    bankrollCents: STARTING_BANKROLL_CENTS,
    activeGame: null,
    lobby: { lastTableId: 'craps:standard' },
    games: {
      craps: {
        tables: {
          standard:     unlockedStandardTable(CRAPS_STANDARD_RULES,   10_000),  // default $100 chip
          'high-limit': lockedTable(CRAPS_HIGH_LIMIT_RULES,           50_000),  // default $500 chip
          'whale':      lockedTable(CRAPS_WHALE_RULES,                100_000), // default $1k chip
        },
      },
    },
  };
}

// Shallow-merge persisted tables on top of fresh defaults so stale saves
// missing newer keys (e.g. high-limit/whale tables added in a later release)
// inherit them automatically. `rules` is always overwritten with the current
// defaults — they aren't user-customizable, so a stale persisted ruleset
// would otherwise keep returning users to outdated stakes.
function mergeTables<T extends { rules: unknown }>(
  freshTables: Record<string, T>,
  parsedTables: Record<string, T> | undefined,
): Record<string, T> {
  const out: Record<string, T> = { ...freshTables };
  for (const [key, table] of Object.entries(parsedTables ?? {})) {
    const freshTable = freshTables[key];
    // Drop any legacy `session` blob — single-active-game uses the root
    // activeGame pointer now; stale per-table sessions are discarded on load.
    const { session: _legacy, ...tableNoSession } = table as T & { session?: unknown };
    out[key] = freshTable
      ? { ...freshTable, ...tableNoSession, rules: freshTable.rules }
      : (tableNoSession as T);
  }
  return out;
}

// Hydrate a partial PersistedV2 (v1-migrated, or stale v2) up to the current
// schema by overlaying parsed tables onto fresh defaults, then reconciling
// retroactive unlocks for bankrolls that already exceed a tier's gate.
function hydratePersistedV2(parsed: PersistedV2): PersistedV2 {
  const fresh = freshDefaults();
  const merged: PersistedV2 = {
    ...fresh,
    ...parsed,
    activeGame: parsed.activeGame ?? null,
    games: {
      craps: { tables: mergeTables(fresh.games.craps.tables, parsed.games?.craps?.tables) },
    },
  };
  return reconcileUnlocks(merged);
}

export function load(): PersistedV2 {
  // 1. v2 present → use it.
  try {
    const raw = localStorage.getItem(PERSISTENCE_KEY_V2);
    if (raw) {
      return hydratePersistedV2(JSON.parse(raw) as PersistedV2);
    }
  } catch {
    // fall through
  }

  // 2. v1 present → migrate, save v2, drop v1.
  try {
    const raw = localStorage.getItem(PERSISTENCE_KEY_V1);
    if (raw) {
      const v1 = JSON.parse(raw) as V1Snapshot;
      const v2 = migrateV1ToV2(v1);
      const hydrated = hydratePersistedV2(v2);
      save(hydrated);
      localStorage.removeItem(PERSISTENCE_KEY_V1);
      return hydrated;
    }
  } catch {
    // fall through
  }

  // 3. Nothing — return fresh defaults.
  return freshDefaults();
}

export function save(state: PersistedV2): void {
  try {
    localStorage.setItem(PERSISTENCE_KEY_V2, JSON.stringify(state));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('localStorage save failed', e);
  }
}

export function reset(): void {
  localStorage.removeItem(PERSISTENCE_KEY_V1);
  localStorage.removeItem(PERSISTENCE_KEY_V2);
  // Wipe transient UI-state flags that key off persistence — e.g. the
  // bust-recovery modal's once-per-session lock. Without this, RESET
  // from the lobby's PlayerCard (which doesn't know about the bust
  // modal) would leave the suppression flag set and silently swallow
  // the modal on the fresh session's first dip.
  try { sessionStorage.removeItem('casino.bust_modal_shown'); } catch {}
}

// ---- helpers used by bridges ----

export function getTableRules<T>(state: PersistedV2, gameId: GameId, tableKey = 'standard'): T {
  const t = state.games[gameId]?.tables[tableKey];
  if (!t) throw new Error(`No table ${gameId}:${tableKey} in persisted state`);
  return t.rules as T;
}

export function getTablePrefs(state: PersistedV2, gameId: GameId, tableKey = 'standard'): TablePrefs {
  const t = state.games[gameId]?.tables[tableKey];
  if (!t) throw new Error(`No table ${gameId}:${tableKey} in persisted state`);
  return { ...t.prefs };
}

export function setBankroll(state: PersistedV2, cents: number): PersistedV2 {
  return { ...state, bankrollCents: cents };
}

export function setSelectedDenom(
  state: PersistedV2, gameId: GameId, tableKey: string, cents: number,
): PersistedV2 {
  const game = state.games[gameId];
  const table = game.tables[tableKey];
  if (!table) return state;
  return {
    ...state,
    games: {
      ...state.games,
      [gameId]: {
        ...game,
        tables: {
          ...game.tables,
          [tableKey]: { ...table, prefs: { ...table.prefs, selectedDenomCents: cents } },
        },
      },
    },
  };
}

export function setLastTableId(state: PersistedV2, id: TableId): PersistedV2 {
  return { ...state, lobby: { ...state.lobby, lastTableId: id } };
}

const HIGH_LIMIT_GATE_CENTS = 500_000;   // $5,000
const WHALE_GATE_CENTS = 5_000_000;      // $50,000

/**
 * Idempotent retroactive-unlock check. For each tier-table that's still
 * locked, if the current `bankrollCents` (or an explicit `cents` value)
 * already exceeds the tier's gate, flip `unlocked: true`. Used at `load()`
 * time so a save written before this feature shipped — where bankroll might
 * already be above $5k or $50k — gets its tables unlocked without needing a
 * fresh BankrollChanged event.
 *
 * `unlockSeenInLobby` stays `false` for retroactively-flipped tables, so
 * the lobby still plays the one-time velvet-rope-drop animation on next visit.
 */
export function reconcileUnlocks(state: PersistedV2, cents: number = state.bankrollCents): PersistedV2 {
  const now = Date.now();
  const advance = <T>(table: PersistedTable<T>, gateCents: number): PersistedTable<T> => {
    if (table.unlocked) return table;
    if (cents < gateCents) return table;
    return { ...table, unlocked: true, unlockedAt: now };
  };
  const nextCraps = {
    tables: {
      ...state.games.craps.tables,
      'high-limit': advance(state.games.craps.tables['high-limit']!, HIGH_LIMIT_GATE_CENTS),
      'whale':      advance(state.games.craps.tables['whale']!,      WHALE_GATE_CENTS),
    },
  };
  return { ...state, games: { craps: nextCraps } };
}

/**
 * Flip tier-unlock flags for any gate the given `cents` crosses, WITHOUT
 * touching the floor wallet. Used mid-game against the live in-game bankroll
 * so the "tier unlocked" toast still fires at the table. Returns the new
 * state plus the tableIds that just transitioned (for the toast).
 */
export function checkUnlocks(
  prev: PersistedV2, cents: number,
): { next: PersistedV2; unlocked: TableId[] } {
  const now = Date.now();
  const unlocked: TableId[] = [];
  const advance = <T>(table: PersistedTable<T>, gateCents: number, tableId: TableId): PersistedTable<T> => {
    if (table.unlocked) return table;
    if (cents < gateCents) return table;
    unlocked.push(tableId);
    return { ...table, unlocked: true, unlockedAt: now };
  };
  const nextCraps = {
    tables: {
      ...prev.games.craps.tables,
      'high-limit': advance(prev.games.craps.tables['high-limit']!, HIGH_LIMIT_GATE_CENTS, 'craps:high-limit'),
      'whale':      advance(prev.games.craps.tables['whale']!,      WHALE_GATE_CENTS,      'craps:whale'),
    },
  };
  return { next: { ...prev, games: { craps: nextCraps } }, unlocked };
}

// ---- activeGame accessors ----

export function getActiveGame(state: PersistedV2): ActiveGame | null {
  return state.activeGame;
}

export function startActiveGame(state: PersistedV2, game: ActiveGame): PersistedV2 {
  return { ...state, activeGame: game };
}

export function updateActiveSnapshot(state: PersistedV2, snapshot: unknown): PersistedV2 {
  if (!state.activeGame) return state;
  return { ...state, activeGame: { ...state.activeGame, snapshot } };
}

export function setActiveGameTurnState(state: PersistedV2, turnId: number, lastSeq: number): PersistedV2 {
  if (!state.activeGame) return state;
  return { ...state, activeGame: { ...state.activeGame, turnId, lastSeq } };
}

export function clearActiveGame(state: PersistedV2): PersistedV2 {
  return { ...state, activeGame: null };
}

/**
 * End the active game: write the floor wallet from the reconciled final
 * cash-in-hand, reconcile tier unlocks against it, and clear activeGame.
 */
export function endActiveGame(state: PersistedV2, finalBankrollCents: number): PersistedV2 {
  const withWallet: PersistedV2 = { ...state, bankrollCents: finalBankrollCents, activeGame: null };
  return reconcileUnlocks(withWallet, finalBankrollCents);
}

/**
 * Highest tier that's unlocked for the player. A single bankroll threshold
 * gates each tier-table.
 */
export function getPlayerTier(state: PersistedV2): 'standard' | 'high-limit' | 'whale' {
  const isUnlocked = (tableKey: 'high-limit' | 'whale') =>
    state.games.craps.tables[tableKey]?.unlocked;
  if (isUnlocked('whale')) return 'whale';
  if (isUnlocked('high-limit')) return 'high-limit';
  return 'standard';
}

/**
 * Flip the per-tile `unlockSeenInLobby` flag — called by the lobby scene
 * after it has played the one-time velvet-rope-drop animation for a tile.
 */
export function markUnlockSeenInLobby(state: PersistedV2, tableId: TableId): PersistedV2 {
  const [gameId, tableKey] = tableId.split(':') as [GameId, string];
  const game = state.games[gameId];
  const table = game?.tables[tableKey];
  if (!game || !table) return state;
  return {
    ...state,
    games: {
      ...state.games,
      [gameId]: {
        ...game,
        tables: {
          ...game.tables,
          [tableKey]: { ...table, unlockSeenInLobby: true },
        },
      },
    },
  };
}
